import PrepaidCardSaleApi from '@apis/prepaid/PrepaidCardSaleApi';
import PrepaidContext from '@components/prepaid/PrepaidProvider/PrepaidContext';
import { datadogRum } from '@datadog/browser-rum';
import useFingerPay from '@hooks/order/useFingerPay';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

function usePrepaidCharge(id: string) {
    const { t } = useTranslation('prepaidCard');
    const { amount } = useContext(PrepaidContext);
    const toast = useToast();
    const projectId = useProjectId();
    const openFingerPay = useFingerPay();

    const { mutate } = useMutation({
        mutationFn: () => PrepaidCardSaleApi.registerCharge(id, {
            projectId,
            amount: amount * 10000,
            payMethodType: 'CREDIT_CARD',
        }),
        onSuccess(res) {
            const {
                checkoutUrl, orderNo, mallId, requestedAt, hashString,
            } = res;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            } else if (orderNo && mallId && requestedAt && hashString) {
                openFingerPay({
                    orderNo,
                    mallId,
                    requestedAt,
                    hashString,
                    paymentAmount: amount * 10000,
                    itemName: `${t('charge.itemName')} ${(amount * 10000).toLocaleString()}${t('currencyUnit')}`,
                    returnUri: '/prepaid-card/payment/finger-pay',
                });
            }
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('charge.error'));
        },
    });
    const handleCharge = () => {
        if (import.meta.env.MODE !== 'development') {
            datadogRum.addAction('prepaidCardChargeCheckout', {
                amount,
            });
        }
        mutate();
    };

    return { handleCharge };
}

export default usePrepaidCharge;
